import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";
import { optionalAuthMiddlewareServer } from "../middleware/optional-auth";

// SCHEMAS
export const getProgramSchema = z.object({
  programId: z.string(),
});

export const getProgramDaySchema = z.object({
  dayId: z.string(),
});

export const getWeekDaysSchema = z.object({
  programId: z.string(),
  weekNumber: z.number().int().min(1),
});

export const getProgramByCodeSchema = z.object({
  code: z.string(),
});

// FUNCTIONS
export const listProgramsFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .handler(async ({ context }) => {
    const { programService } = await import("@/api/services/program.service");
    const userId = context?.auth?.userId;
    const programs = await programService.listPublicPrograms(userId);
    return {
      status: 200,
      body: {
        programs,
        total: programs.length,
      },
    };
  });

export const getProgramFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .inputValidator(getProgramSchema)
  .handler(async ({ context, data }) => {
    const { programService } = await import("@/api/services/program.service");
    const { catalogAccessService } = await import(
      "@/api/services/catalog-access.service"
    );

    const program = await programService.getProgramById(data.programId);

    if (!program) {
      return {
        status: 404,
        body: { message: "Program bulunamadı" },
      };
    }

    if (context.auth?.role !== "admin") {
      const access = await catalogAccessService.getProgramAccess(
        data.programId,
        context.auth?.userId,
      );
      if (!access.hasAccess) {
        const { days: _omittedDays, ...publicInfo } = program;
        return {
          status: 200,
          body: {
            ...publicInfo,
            accessCode: null,
            days: [],
            hasAccess: false,
            requiresPurchase: access.requiresPurchase,
            catalogItemId: access.catalogItemId,
            priceKurus: access.priceKurus,
            currency: access.currency || "TRY",
          },
        };
      }
    }

    return {
      status: 200,
      body: {
        ...program,
        accessCode: context.auth?.role === "admin" ? program.accessCode : null,
        hasAccess: true,
        requiresPurchase: false,
      },
    };
  });

export const getProgramDayFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .inputValidator(getProgramDaySchema)
  .handler(async ({ context, data }) => {
    const { programService } = await import("@/api/services/program.service");
    const { exerciseService } = await import("@/api/services/exercise.service");
    const day = await programService.getProgramDayById(data.dayId);

    if (!day) {
      return {
        status: 404,
        body: { message: "Gün bulunamadı" },
      };
    }

    if (context.auth?.role !== "admin") {
      const { catalogAccessService } = await import(
        "@/api/services/catalog-access.service"
      );
      const access = await catalogAccessService.getProgramAccess(
        day.programId,
        context.auth?.userId,
      );
      if (!access.hasAccess) {
        return {
          status: 403,
          body: { message: "Bu antrenman gününe erişim yetkiniz yok" },
        };
      }
    }

    // Fetch exercise details
    const exerciseIds = day.exercises
      .map((e) => e.exerciseId)
      .filter((id): id is string => !!id);

    // Get unique IDs
    const uniqueIds = [...new Set(exerciseIds)];

    if (uniqueIds.length === 0) {
      return {
        status: 200,
        body: {
          ...day,
          exercises: day.exercises.map((e) => ({
            ...e,
            exerciseInfo: undefined,
          })),
        },
      };
    }

    const exerciseDetails = await exerciseService.getExercisesByIds(uniqueIds);

    // Merge details
    const exercisesWithDetails = day.exercises.map((e) => {
      const details = exerciseDetails.find(
        (d) => d.exerciseId === e.exerciseId,
      );
      if (!details) return { ...e, exerciseInfo: undefined };

      return {
        ...e,
        exerciseInfo: {
          videoUrl: details.videoUrl,
          descriptionUrl: details.descriptionUrl,
          instructions: details.instructions,
          muscleGroup: details.muscleGroup ?? undefined,
        },
      };
    });

    return {
      status: 200,
      body: {
        ...day,
        exercises: exercisesWithDetails,
      },
    };
  });

export const getWeekDaysFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .inputValidator(getWeekDaysSchema)
  .handler(async ({ context, data }) => {
    if (context.auth?.role !== "admin") {
      const { catalogAccessService } = await import(
        "@/api/services/catalog-access.service"
      );
      const access = await catalogAccessService.getProgramAccess(
        data.programId,
        context.auth?.userId,
      );
      if (!access.hasAccess) {
        return {
          status: 403,
          body: { message: "Bu program haftalarına erişim yetkiniz yok" },
        };
      }
    }
    const { programService } = await import("@/api/services/program.service");
    const days = await programService.getWeekDays(
      data.programId,
      data.weekNumber,
    );

    return {
      status: 200,
      body: {
        days,
        total: days.length,
      },
    };
  });

export const listAllProgramsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { programService } = await import("@/api/services/program.service");
    const programs = await programService.listAllPrograms();
    return {
      status: 200,
      body: {
        programs,
        total: programs.length,
      },
    };
  });

export const getProgramByCodeFn = createServerFn({ method: "GET" })
  .middleware([optionalAuthMiddlewareServer])
  .inputValidator(getProgramByCodeSchema)
  .handler(async ({ data }) => {
    const { programService } = await import("@/api/services/program.service");
    const program = await programService.getProgramByCode(data.code);

    if (!program) {
      return {
        status: 404,
        body: { message: "Geçersiz program kodu" },
      };
    }

    const { accessCode: _accessCode, days: _days, ...preview } = program;

    return {
      status: 200,
      body: preview,
    };
  });

export const getProgramParticipantCountFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getProgramSchema)
  .handler(async ({ data }) => {
    const { userProgramService } = await import(
      "@/api/services/user-program.service"
    );
    const count = await userProgramService.getProgramParticipantCount(
      data.programId,
    );

    return {
      status: 200,
      body: { count },
    };
  });
